/**
 * The rooms this browser was in, remembered across a refresh.
 *
 * A reconnection has always restored the open rooms, because the tab was still
 * there holding the list. A refresh had nothing to restore from: the page came
 * back, signed itself in with the stored token, and opened the default room -
 * so somebody who kept four rooms open and pressed reload got one, and had to
 * find the other three by hand. This is the list that makes those two paths
 * behave the same way.
 *
 * Two things are worth explaining about the shape of what is stored.
 *
 * It is scoped to a nickname. Room names are not always public - a hidden room
 * is hidden by not being listed, and knowing the name is the whole of the way
 * in - so a list left behind in a shared browser must not open for whoever signs
 * in next. A stored list belonging to a different nickname is ignored and
 * discarded rather than restored.
 *
 * And it holds only names. What was said in a room is the server's to replay on
 * rejoin; keeping any of it here would put a transcript in localStorage, on a
 * server whose whole retention model is built on not keeping one.
 */
const STORAGE_KEY = 'webchat.rooms';

/**
 * How many rooms to restore at most.
 *
 * The server refuses a connection more than MAX_ROOMS_PER_CONNECTION rooms, and
 * a restore that walked into that would report a failure for each one over the
 * line. The real limit is read from the server's policy where it is known; this
 * is only the backstop for a restore that runs before the policy has arrived.
 */
const MAX_ROOMS = 8;

function readRaw() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return parsed;
  } catch {
    // Unreadable or unparseable: treat it as nothing rather than let a corrupt
    // entry stop the page from starting.
    return null;
  }
}

/** Normalises to the form room names take on the wire: no #, lower case. */
function normalise(name) {
  return String(name ?? '')
    .replace(/^#+/, '')
    .trim()
    .toLowerCase();
}

function keyFor(nickname) {
  return normalise(nickname);
}

/**
 * Remembers the rooms `nickname` has open, and which one they were looking at.
 * Called on every join and leave, so the stored list is never behind the tab.
 */
export function saveRooms(nickname, rooms, activeRoom = null) {
  const owner = keyFor(nickname);
  if (!owner) return null;

  const names = [...new Set((rooms ?? []).map(normalise).filter(Boolean))].slice(0, MAX_ROOMS);
  const entry = {
    owner,
    rooms: names,
    active: names.includes(normalise(activeRoom)) ? normalise(activeRoom) : (names[0] ?? null),
    at: Date.now(),
  };

  try {
    if (names.length === 0) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // A browser with storage denied or full still works; it just forgets.
  }
  return entry;
}

/**
 * The rooms stored for `nickname`, or an empty list.
 *
 * A list belonging to somebody else is not returned and not kept: the next
 * person to use this browser should neither inherit the rooms nor leave the
 * names sitting there to be found.
 */
export function loadRooms(nickname, { limit = MAX_ROOMS } = {}) {
  const owner = keyFor(nickname);
  const entry = readRaw();
  if (!entry || !owner) return { rooms: [], active: null };

  if (entry.owner !== owner) {
    clearRooms();
    return { rooms: [], active: null };
  }

  const rooms = Array.isArray(entry.rooms)
    ? [...new Set(entry.rooms.map(normalise).filter(Boolean))].slice(0, Math.max(0, limit))
    : [];
  const active = rooms.includes(normalise(entry.active)) ? normalise(entry.active) : (rooms[0] ?? null);
  return { rooms, active };
}

/** Forgets one room, for a rejoin that was refused - a ban, or a room now gone. */
export function forgetRoom(nickname, room) {
  const { rooms, active } = loadRooms(nickname);
  const target = normalise(room);
  const remaining = rooms.filter((name) => name !== target);
  if (remaining.length === rooms.length) return { rooms, active };
  return saveRooms(nickname, remaining, active === target ? null : active) ?? { rooms: [], active: null };
}

/** Drops the list entirely. Signing out does this, as does a change of owner. */
export function clearRooms() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to clear if it cannot be reached */
  }
}

export default { saveRooms, loadRooms, forgetRoom, clearRooms, MAX_ROOMS };
